import * as React from 'react';

// --- Lib ---
import { parseShortcutKeys } from '../../../../app/rte/lib/tiptap-utils';

// --- Hooks ---
import { useTiptapEditor } from '../../../../app/rte/hooks/use-tiptap-editor';

// --- Tiptap UI ---
import type { UseDeleteNodeConfig } from './use-delete-node';
import { DELETE_NODE_SHORTCUT_KEY, useDeleteNode } from './use-delete-node';

// --- UI Components ---
import { Button } from '@cotera/client/v0/components/ui/button';
import { Badge } from '@cotera/client/v0/components/ui/badge';
import { Icon } from '@cotera/client/app/components';

export interface DeleteNodeButtonProps
  extends Omit<React.ComponentProps<typeof Button>, 'type'>,
    UseDeleteNodeConfig {
  /**
   * Optional text to display alongside the icon.
   */
  text?: string;
  /**
   * Optional show shortcut keys in the button.
   * @default false
   */
  showShortcut?: boolean;
}

export function DeleteNodeShortcutBadge({
  shortcutKeys = DELETE_NODE_SHORTCUT_KEY,
}: {
  shortcutKeys?: string;
}) {
  return <Badge variant="outline">{parseShortcutKeys({ shortcutKeys })}</Badge>;
}

/**
 * Button component for deleting a node in a Tiptap editor.
 *
 * For custom button implementations, use the `useDeleteNode` hook instead.
 */
export const DeleteNodeButton = React.forwardRef<
  HTMLButtonElement,
  DeleteNodeButtonProps
>(
  (
    {
      editor: providedEditor,
      text,
      hideWhenUnavailable = false,
      onDeleted,
      showShortcut = false,
      onClick,
      children,
      ...buttonProps
    },
    ref
  ) => {
    const { editor } = useTiptapEditor(providedEditor);
    const {
      isVisible,
      handleDeleteNode,
      label,
      shortcutKeys,
      Icon: iconName,
    } = useDeleteNode({
      editor,
      hideWhenUnavailable,
      onDeleted,
    });

    const handleClick = React.useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(event);
        if (event.defaultPrevented) {
          return;
        }
        handleDeleteNode();
      },
      [handleDeleteNode, onClick]
    );

    if (!isVisible) {
      return null;
    }

    return (
      <Button
        type="button"
        variant="ghost"
        role="button"
        tabIndex={-1}
        aria-label={label}
        title="Delete"
        onClick={handleClick}
        {...buttonProps}
        ref={ref}
      >
        {children ?? (
          <>
            <Icon icon={iconName} className="tiptap-button-icon" />
            {text && <span className="tiptap-button-text">{text}</span>}
            {showShortcut && (
              <DeleteNodeShortcutBadge shortcutKeys={shortcutKeys} />
            )}
          </>
        )}
      </Button>
    );
  }
);

DeleteNodeButton.displayName = 'DeleteNodeButton';
